import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import html2pdf from 'html2pdf.js';
import {
  CheckCircle, FileText, ShoppingBag, Download, Printer, X,
  MapPin, Phone, Mail, Smartphone, ShieldCheck, ArrowLeft,
  ChevronRight, Lock, Star, Package, Truck, Search, User, Loader2
} from 'lucide-react';
import logo from './Images/Logo.jpeg';

// ─────────────────────────────────────────────────────────────────────────────
// BASE URL — change this once here if your domain ever changes
// ─────────────────────────────────────────────────────────────────────────────
const API_BASE = 'https://dgl-core-9x7.dailygolive.in/api';

// ─────────────────────────────────────────────────────────────────────────────
// CUSTOMER SEARCH HOOK
// Queries /api/orders/customers/search (lives inside orders router)
// Handles debounce, abort, and response-shape normalisation
// ─────────────────────────────────────────────────────────────────────────────
const useCustomerSearch = () => {
  const [results,    setResults]    = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [searchErr,  setSearchErr]  = useState(null);
  const debounceRef  = useRef(null);
  const abortRef     = useRef(null);

  // Accept any response shape the server might return
  const extractArray = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    for (const k of ['customers', 'results', 'data', 'orders']) {
      if (Array.isArray(data[k])) return data[k];
    }
    if (data._id || data.id) return [data];
    return [];
  };

  // Flatten nested customerDetails if present
  const flatten = (c) => {
    const cd = (typeof c.customerDetails === 'object' && c.customerDetails) ? c.customerDetails : c;
    return {
      _id:          c._id          || c.id             || String(Math.random()),
      shopName:     cd.shopName    || c.shopName        || '',
      ownerName:    cd.ownerName   || c.ownerName       || '',
      mobileNumber: cd.mobileNumber|| c.mobileNumber    || '',
      address:      cd.address     || c.address         || '',
      fos:          cd.fos         || c.fos             || '',
    };
  };

  const search = useCallback((query, field) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query || query.trim().length < 2) {
      setResults([]);
      setSearchErr(null);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();
      setLoading(true);
      setSearchErr(null);
      try {
        const url = `${API_BASE}/orders/customers/search?${new URLSearchParams({ [field]: query.trim() })}`;
        const res = await fetch(url, { signal: abortRef.current.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setResults(extractArray(data).map(flatten));
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('Customer search failed:', err);
        setSearchErr('Search failed. Please try again.');
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 400);
  }, []);

  const clear = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current)    abortRef.current.abort();
    setResults([]);
    setLoading(false);
    setSearchErr(null);
  }, []);

  return { results, loading, searchErr, search, clear };
};

// ─────────────────────────────────────────────────────────────────────────────
// DROPDOWN — standalone so it never remounts when parent re-renders
// ─────────────────────────────────────────────────────────────────────────────
const CustomerDropdown = React.memo(({ results, loading, searchErr, query, onSelect }) => {
  if (!query || query.trim().length < 2) return null;
  if (!loading && results.length === 0 && !searchErr) return null;

  return (
    <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-md shadow-xl z-[300] overflow-hidden max-h-72 overflow-y-auto">
      {loading ? (
        <div className="flex items-center gap-2 px-4 py-3 text-sm text-gray-500">
          <Loader2 size={14} className="animate-spin text-[#e77600] shrink-0" />
          Searching customers…
        </div>
      ) : searchErr ? (
        <div className="px-4 py-3 text-xs text-red-500 font-medium">{searchErr}</div>
      ) : (
        results.map((c, i) => (
          <button
            key={c._id || i}
            type="button"
            onMouseDown={(e) => { e.preventDefault(); onSelect(c); }}
            className="w-full text-left px-4 py-3 hover:bg-[#fff8e7] border-b border-gray-100 last:border-0 transition-colors"
          >
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-[#FF9900]/15 border border-[#FF9900]/30 flex items-center justify-center shrink-0 mt-0.5">
                <User size={13} className="text-[#e77600]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-gray-900 truncate">{c.shopName || '—'}</p>
                <p className="text-xs text-gray-500 truncate">
                  {c.ownerName || '—'}
                  {c.mobileNumber && (
                    <span className="ml-2 text-[#007185] font-medium">{c.mobileNumber}</span>
                  )}
                </p>
                {c.address && (
                  <p className="text-[11px] text-gray-400 truncate mt-0.5">
                    <MapPin size={9} className="inline mr-0.5 -mt-0.5" />
                    {c.address}
                  </p>
                )}
              </div>
            </div>
          </button>
        ))
      )}
    </div>
  );
});
CustomerDropdown.displayName = 'CustomerDropdown';

// ─────────────────────────────────────────────────────────────────────────────
// SEARCHABLE INPUT — defined OUTSIDE PaymentPage so React never remounts it
// ─────────────────────────────────────────────────────────────────────────────
const SearchableInput = React.memo(({
  name, label, type, placeholder, required, maxLength, pattern,
  value, onChange, onFocus, onBlur,
  isActive, loading, results, searchErr, onSelect,
  customerSelected, containerRef,
  inputCls, labelCls, autoFilledCls,
}) => (
  <div className="relative" ref={containerRef}>
    <label className={labelCls}>
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
      {isActive && loading && (
        <span className="ml-2 text-[11px] text-[#007185] font-normal inline-flex items-center gap-1">
          <Loader2 size={11} className="animate-spin" />
          Searching…
        </span>
      )}
    </label>
    <div className="relative">
      <input
        type={type || 'text'}
        name={name}
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        required={required}
        maxLength={maxLength}
        pattern={pattern}
        autoComplete="off"
        placeholder={placeholder}
        className={inputCls + (customerSelected ? autoFilledCls : '') + ' pr-8'}
      />
      <Search
        size={13}
        className={`absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none transition-colors ${
          isActive ? 'text-[#e77600]' : 'text-gray-300'
        }`}
      />
    </div>
    {isActive && (
      <CustomerDropdown
        results={results}
        loading={loading}
        searchErr={searchErr}
        query={value}
        onSelect={onSelect}
      />
    )}
  </div>
));
SearchableInput.displayName = 'SearchableInput';

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAYMENT PAGE
// ─────────────────────────────────────────────────────────────────────────────
const PaymentPage = () => {
  const location = useLocation();
  const navigate  = useNavigate();

  // ── SUCCESS STATE ─────────────────────────────────────────
  const [isSuccess,      setIsSuccess]      = useState(false);
  const [savedOrderData, setSavedOrderData] = useState(null);
  const [showInvoice,    setShowInvoice]    = useState(false);

  // ── ORDER SUMMARY ─────────────────────────────────────────
  const [orderSummary] = useState({
    items:    location.state?.items    || [],
    discount: location.state?.discount || 0,
    tax:      location.state?.tax      || 0,
  });
  const subtotal = orderSummary.items.reduce((s, i) => s + Number(i.price) * Number(i.qty), 0);
  const total    = subtotal - orderSummary.discount + orderSummary.tax;

  // ── FORM DATA ─────────────────────────────────────────────
  const [formData, setFormData] = useState({
    shopImage: null, shopName: '', ownerName: '',
    mobileNumber: '', address: '', fos: '',
  });

  // ── SEARCH ────────────────────────────────────────────────
  const { results, loading, searchErr, search, clear } = useCustomerSearch();
  const [activeField,       setActiveField]       = useState(null);
  const [customerSelected,  setCustomerSelected]  = useState(false);

  // One ref per searchable field for click-outside detection
  const refs = {
    shopName:     useRef(null),
    ownerName:    useRef(null),
    mobileNumber: useRef(null),
  };
  const SEARCHABLE = ['shopName', 'ownerName', 'mobileNumber'];

  useEffect(() => {
    const outside = (e) => {
      const inside = Object.values(refs).some(r => r.current?.contains(e.target));
      if (!inside) { setActiveField(null); clear(); }
    };
    document.addEventListener('mousedown', outside);
    return () => document.removeEventListener('mousedown', outside);
  }, [clear]);

  const handleCustomerSelect = useCallback((c) => {
    setFormData(prev => ({
      ...prev,
      shopName:     c.shopName     || prev.shopName,
      ownerName:    c.ownerName    || prev.ownerName,
      mobileNumber: c.mobileNumber || prev.mobileNumber,
      address:      c.address      || prev.address,
      fos:          c.fos          || prev.fos,
    }));
    setCustomerSelected(true);
    setActiveField(null);
    clear();
  }, [clear]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setCustomerSelected(false);
    if (SEARCHABLE.includes(name)) {
      setActiveField(name);
      search(value, name);
    }
  }, [search]);

  const handleFieldFocus = useCallback((name) => {
    if (!SEARCHABLE.includes(name)) return;
    const val = formData[name] || '';
    if (val.trim().length >= 2 && !customerSelected) {
      setActiveField(name);
      search(val, name);
    }
  }, [formData, customerSelected, search]);

  // Delay 200ms so dropdown's onMouseDown fires before blur closes it
  const handleFieldBlur = useCallback(() => {
    setTimeout(() => setActiveField(null), 200);
  }, []);

  // ── PAYMENT & OTHER STATE ─────────────────────────────────
  const [locationStr,   setLocationStr]   = useState('');
  const [isLocating,    setIsLocating]    = useState(false);
  const [isSubmitting,  setIsSubmitting]  = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentType,   setPaymentType]   = useState('full');
  const [partialAmount, setPartialAmount] = useState('');
  const [screenshot,    setScreenshot]    = useState(null);
  const [transactionId, setTransactionId] = useState('');

  const amountToPay =
    paymentMethod === 'cod' ? 0
    : paymentType === 'partial' && Number(partialAmount) >= 100
      ? Number(partialAmount) : total;

  const calculateBalance = () => {
    if (paymentMethod === 'cod') return total;
    if (paymentType === 'partial' && partialAmount) {
      const rem = total - Number(partialAmount);
      return rem > 0 ? rem : 0;
    }
    return 0;
  };

  const UPI_ID = 'dailygopvtltd@idfcbank';
  const PAYEE  = 'Daily Go Pvt Ltd';

  const buildUpiParams = (amount) => {
    const a = Number(amount).toFixed(2);
    const n = encodeURIComponent('Payment to Daily Go Pvt Ltd');
    return `pa=${UPI_ID}&pn=${encodeURIComponent(PAYEE)}&am=${a}&cu=INR&tn=${n}`;
  };

  const getDeepLink = (app) => {
    const p = buildUpiParams(amountToPay);
    const ios = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (app === 'gpay')    return ios ? `gpay://upi/pay?${p}` : `tez://upi/pay?${p}`;
    if (app === 'phonepe') return `phonepe://pay?${p}`;
    return `upi://pay?${p}`;
  };

  const handlePayViaApp = (e) => {
    e.preventDefault();
    if (!amountToPay || amountToPay <= 0) return alert('Please enter a valid amount.');
    if (paymentType === 'partial' && Number(partialAmount) > total)
      return alert(`Amount cannot exceed ₹${total.toFixed(2)}.`);
    if (paymentType === 'partial' && Number(partialAmount) < 100)
      return alert('Minimum partial payment is ₹100.');
    const dl = getDeepLink(paymentMethod);
    const fb = `upi://pay?${buildUpiParams(amountToPay)}`;
    window.location.href = dl;
    const t = setTimeout(() => { window.location.href = fb; }, 1500);
    const fn = () => { if (document.hidden) { clearTimeout(t); document.removeEventListener('visibilitychange', fn); } };
    document.addEventListener('visibilitychange', fn);
  };

  const validateFileSize = (f) => !f || f.size / (1024 * 1024) <= 5;

  const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    if (!validateFileSize(file)) return alert('File must be under 5MB');
    if (field === 'shopImage') setFormData(p => ({ ...p, shopImage: file }));
    else if (field === 'screenshot') setScreenshot(file);
  };

  const detectLocation = () => {
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocationStr(`Lat: ${pos.coords.latitude.toFixed(4)}, Lng: ${pos.coords.longitude.toFixed(4)}`);
        setIsLocating(false);
      },
      () => { alert('Enable location access'); setIsLocating(false); }
    );
  };

  const handleDownloadPDF = () => {
    html2pdf()
      .set({
        margin: 0,
        filename: `Invoice_${savedOrderData?.invoiceNo || 'Order'}.pdf`,
        image: { type: 'jpeg', quality: 1 },
        html2canvas: { scale: 3, useCORS: true, letterRendering: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      })
      .from(document.getElementById('printable-bill'))
      .save();
  };

  const formatDate = (d) => {
    if (!d) return new Date().toLocaleDateString('en-IN');
    return new Date(d).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!paymentMethod)             return alert('Please select a payment method.');
    if (!locationStr)               return alert('Please detect your location.');
    if (!formData.shopImage)        return alert('Please upload a shop image.');
    if (!/^\d{10}$/.test(formData.mobileNumber))
                                    return alert('Please enter a valid 10-digit mobile number.');
    if (paymentMethod !== 'cod') {
      if (paymentType === 'partial') {
        const p = Number(partialAmount);
        if (!partialAmount || p < 100) return alert('Minimum partial payment is ₹100.');
        if (p > total)                 return alert(`Amount cannot exceed ₹${total.toFixed(2)}.`);
      }
      if (!screenshot) return alert('Please upload payment screenshot.');
    }
    setIsSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('shopImage', formData.shopImage);
      if (screenshot) fd.append('screenshot', screenshot);
      const { shopImage, ...cd } = formData;
      fd.append('customerDetails', JSON.stringify(cd));
      fd.append('items', JSON.stringify(
        orderSummary.items.map(i => ({ id: i.id, name: i.name, price: Number(i.price), qty: Number(i.qty) }))
      ));
      fd.append('payment', JSON.stringify({
        method: paymentMethod, type: paymentType,
        amountPaid: paymentMethod === 'cod' ? 0 : Number(amountToPay),
        transactionId: transactionId || '',
        balance: Number(calculateBalance()),
        status: paymentMethod === 'cod' ? 'due' : 'pending',
      }));
      fd.append('totals', JSON.stringify({ subtotal, discount: orderSummary.discount, tax: orderSummary.tax, total }));
      fd.append('location', locationStr);

      const res  = await fetch(`${API_BASE}/orders`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || `Server error (${res.status})`);
      setSavedOrderData(data.order || data);
      setIsSuccess(true);
      window.scrollTo(0, 0);
    } catch (err) {
      console.error('Order error:', err);
      alert(err.message || 'Error saving order. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── STYLE TOKENS ──────────────────────────────────────────
  const inputCls =
    'w-full bg-white border border-gray-300 text-gray-900 rounded text-sm px-3 py-2.5 ' +
    'outline-none focus:border-[#e77600] focus:shadow-[0_0_0_3px_rgba(228,121,17,0.25)] transition-all';
  const labelCls    = 'block text-sm font-semibold text-gray-700 mb-1';
  const autoFilledCls = ' border-emerald-400 bg-emerald-50/40';

  // ─────────────────────────────────────────────────────────
  // SUCCESS SCREEN
  // ─────────────────────────────────────────────────────────
  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gray-100 font-sans">
        <div className="max-w-xl mx-auto px-4 py-8">
          <div className="bg-white border border-gray-200 rounded p-6 mb-4 text-center shadow-sm">
            <div className="w-14 h-14 bg-[#FF9900] rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={30} className="text-white" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-1">Order Placed Successfully!</h1>
            <p className="text-sm text-gray-600 mb-1">
              Order <span className="font-bold text-[#007185]">#{savedOrderData?.invoiceNo || '—'}</span> confirmed.
            </p>
            <p className="text-sm text-gray-500">Thank you for your business.</p>
          </div>
          <div className="flex flex-col gap-3">
            <button onClick={() => setShowInvoice(true)}
              className="w-full bg-[#FF9900] hover:bg-[#e88a00] text-[#131921] font-bold py-3 px-6 rounded flex items-center justify-center gap-2 text-sm border border-[#d5a02a] active:scale-[0.98] transition-all">
              <FileText size={16} /> View Invoice
            </button>
            <button onClick={() => navigate('/')}
              className="w-full bg-white hover:bg-gray-50 text-gray-800 font-semibold py-3 px-6 rounded flex items-center justify-center gap-2 text-sm border border-gray-300 active:scale-[0.98] transition-all">
              <ArrowLeft size={16} /> Back to Home
            </button>
          </div>
          <div className="mt-4 bg-[#fff8e1] border border-[#e6b800] rounded p-4 flex gap-3 items-start">
            <Truck size={18} className="text-[#e77600] shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-gray-800">Delivery in progress</p>
              <p className="text-xs text-gray-600 mt-0.5">Our team will contact you shortly.</p>
            </div>
          </div>
        </div>

        {/* INVOICE MODAL */}
        {showInvoice && savedOrderData && (
          <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/70 backdrop-blur-sm overflow-y-auto no-print">
            <div className="bg-white w-full max-w-[850px] my-4 mx-2 rounded shadow-2xl relative">
              <div className="sticky top-0 bg-[#131921] px-4 py-3 flex justify-between items-center z-[110] no-print">
                <div className="flex items-center gap-2 font-bold text-white text-sm">
                  <ShoppingBag size={16} className="text-[#FF9900]" /><span>Invoice</span>
                </div>
                <div className="flex gap-2">
                  <button onClick={handleDownloadPDF}
                    className="flex items-center gap-1.5 bg-[#FF9900] text-[#131921] px-3 py-1.5 rounded text-xs font-black hover:bg-[#e88a00]">
                    <Download size={13} /> PDF
                  </button>
                  <button onClick={() => window.print()}
                    className="flex items-center gap-1.5 bg-gray-600 text-white px-3 py-1.5 rounded text-xs font-black hover:bg-gray-700">
                    <Printer size={13} /> Print
                  </button>
                  <button onClick={() => setShowInvoice(false)}
                    className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded">
                    <X size={18} />
                  </button>
                </div>
              </div>

              <div id="printable-bill" className="bg-white p-8 sm:p-12 text-gray-800 font-sans leading-tight">
                {/* Invoice header */}
                <div className="flex justify-between items-start mb-10">
                  <div className="flex gap-4">
                    <img src={logo} alt="Logo" className="w-16 h-16 object-contain grayscale" />
                    <div>
                      <h1 className="text-xl font-bold text-gray-900 uppercase tracking-tight">Daily Go Pvt Ltd</h1>
                      <div className="text-[10px] text-gray-500 mt-2 space-y-0.5 font-medium leading-relaxed">
                        <p className="flex items-start gap-1 max-w-[300px]">
                          <MapPin size={10} className="mt-0.5 shrink-0" />
                          No 6, Gubbala Main Road, Subramanyapura Post, Bangalore - 560062
                        </p>
                        <p className="flex items-center gap-1"><Phone size={10} /> 9739777166</p>
                        <p className="flex items-center gap-1"><Mail size={10} /> office-info@dailygolive.in</p>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <h2 className="text-4xl font-light text-gray-900 tracking-tighter">INVOICE</h2>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">Original Recipient</p>
                  </div>
                </div>

                {/* GSTIN + Invoice meta */}
                <div className="flex justify-between items-end border-b border-gray-200 pb-3 mb-8">
                  <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                    GSTIN: <span className="text-gray-900">URP</span> | State: <span className="text-gray-900">Karnataka (29)</span>
                  </div>
                  <div className="flex gap-10">
                    <div className="text-right">
                      <p className="text-[9px] text-gray-400 font-bold uppercase">Invoice No</p>
                      <p className="font-bold text-gray-900 text-lg tracking-tighter">{savedOrderData.invoiceNo || 'PENDING'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[9px] text-gray-400 font-bold uppercase">Date Issued</p>
                      <p className="font-bold text-gray-900 text-lg tracking-tighter">{formatDate(savedOrderData.orderDate || savedOrderData.createdAt)}</p>
                    </div>
                  </div>
                </div>

                {/* Billed to */}
                <div className="grid grid-cols-2 gap-10 mb-12">
                  <div className="border border-gray-200 p-6 rounded bg-gray-50">
                    <p className="text-[9px] text-gray-400 font-black uppercase tracking-widest mb-4">Billed To</p>
                    <p className="text-xl font-bold text-gray-900 capitalize mb-1">{savedOrderData.customerDetails?.shopName}</p>
                    <p className="text-xs text-gray-500 leading-normal mb-4 font-medium">{savedOrderData.customerDetails?.address}</p>
                    <p className="text-xs font-bold text-gray-700 flex items-center gap-2">
                      <span className="bg-gray-200 p-1 rounded text-gray-500"><Smartphone size={10} /></span>
                      {savedOrderData.customerDetails?.mobileNumber}
                    </p>
                  </div>
                  <div className="flex flex-col justify-center space-y-3">
                    {[
                      ['Sales Executive', savedOrderData.customerDetails?.fos || 'Direct'],
                      ['GSTIN', 'URP'],
                      ['Place of Supply', 'Bangalore'],
                    ].map(([lbl, val]) => (
                      <div key={lbl} className="flex justify-between text-[11px] border-b border-gray-100 pb-1.5">
                        <span className="text-gray-400 font-bold uppercase tracking-tighter">{lbl}</span>
                        <span className="font-black text-gray-900 uppercase">{val}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Items table */}
                <table className="w-full mb-12 text-left border-collapse">
                  <thead>
                    <tr className="border-y border-gray-200 text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      <th className="py-4 px-2 w-10">#</th>
                      <th className="py-4 px-2">Description</th>
                      <th className="py-4 px-2 text-center w-24">Qty</th>
                      <th className="py-4 px-2 text-right w-32">Rate</th>
                      <th className="py-4 px-2 text-right w-32">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="text-[13px] font-bold text-gray-800">
                    {savedOrderData.items?.map((item, i) => (
                      <tr key={i} className="border-b border-gray-50 last:border-gray-200">
                        <td className="py-5 px-2 text-gray-300 font-medium">{i + 1}</td>
                        <td className="py-5 px-2 text-gray-900">{item.name}</td>
                        <td className="py-5 px-2 text-center">{item.qty}</td>
                        <td className="py-5 px-2 text-right">₹{Number(item.price).toFixed(2)}</td>
                        <td className="py-5 px-2 text-right">₹{(item.qty * item.price).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Footer */}
                <div className="flex justify-between items-start">
                  <div className="w-1/2">
                    <div className="flex items-center gap-6 mb-8">
                      <img
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=upi://pay?pa=9739777166@upi&pn=DailyGo&am=${savedOrderData.totals?.total}`}
                        alt="QR" className="w-24 h-24 border p-1 rounded"
                      />
                      <div className="space-y-2">
                        <div className="flex items-center gap-1.5 text-[10px] font-black text-gray-900 uppercase">
                          <ShieldCheck size={14} className="text-emerald-500" /> Scan to Pay
                        </div>
                        <div className="flex gap-2 opacity-40 grayscale">
                          <img src="https://upload.wikimedia.org/wikipedia/commons/e/e1/UPI-Logo-vector.svg" className="h-3" alt="upi" />
                          <img src="https://upload.wikimedia.org/wikipedia/commons/c/c4/Google_Pay_Logo.svg" className="h-3" alt="gpay" />
                          <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/7/71/PhonePe_Logo.svg/1200px-PhonePe_Logo.svg.png" className="h-3" alt="phonepe" />
                        </div>
                      </div>
                    </div>
                    <div className="text-[9px] text-gray-400 space-y-1 font-medium">
                      <p className="font-black text-gray-500 uppercase mb-1 tracking-widest">Terms &amp; Conditions</p>
                      <p>• Payment is due immediately. No credit facilities available.</p>
                      <p>• Goods once sold cannot be returned.</p>
                      <p>• Disputes subject to Bangalore Jurisdiction.</p>
                    </div>
                  </div>
                  <div className="w-[300px] space-y-4">
                    <div className="flex justify-between text-xs font-bold text-gray-400">
                      <span>Sub Total</span>
                      <span className="text-gray-800">₹{Number(savedOrderData.totals?.subtotal).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-xs font-bold text-gray-400">
                      <span>Discount</span>
                      <span className="text-gray-800">- ₹{Number(savedOrderData.totals?.discount).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                      <span className="text-base font-black text-gray-900 uppercase">Total</span>
                      <span className="text-3xl font-black text-gray-900 italic tracking-tighter">
                        ₹{Number(savedOrderData.totals?.total).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="pt-16 text-center">
                      <div className="border-t border-gray-300 w-full mb-2" />
                      <p className="text-[10px] font-black text-gray-900 uppercase">Authorized Signatory</p>
                      <p className="text-[8px] text-gray-400 font-black uppercase tracking-widest">For Daily Go Pvt Ltd</p>
                    </div>
                  </div>
                </div>

                <div className="mt-16 text-center pt-4 border-t border-gray-100">
                  <p className="text-[10px] text-gray-300 font-black italic uppercase tracking-widest">Thank you for your business</p>
                </div>
              </div>
            </div>
          </div>
        )}

        <style>{`
          @media print {
            body * { visibility: hidden; }
            #printable-bill, #printable-bill * { visibility: visible; }
            #printable-bill { position: absolute; left: 0; top: 0; width: 100%; margin: 0; padding: 20px; }
            .no-print { display: none !important; }
          }
        `}</style>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────
  // CHECKOUT FORM
  // ─────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-100 font-sans mt-32">

      {/* HEADER */}
      {/* <header className="bg-[#131921] px-4 py-3 flex items-center gap-3 sticky top-0 z-50">
        <div className="text-[#FF9900] font-extrabold text-xl tracking-tight leading-none">
          daily<span className="text-white">go</span>
        </div>
        <div className="hidden sm:block h-8 w-px bg-gray-600 mx-2" />
        <span className="hidden sm:block text-white text-lg font-semibold">Checkout</span>
        <div className="flex items-center gap-1 ml-auto text-xs text-gray-300">
          <Lock size={12} className="text-[#FF9900]" />
          <span className="hidden sm:inline">Secure Checkout</span>
        </div>
      </header> */}

      {/* BREADCRUMB */}
      <div className="bg-gray-100 border-b border-gray-200 px-4 py-2">
        <div className="max-w-4xl mx-auto flex items-center gap-1 text-xs sm:text-sm overflow-x-auto whitespace-nowrap">
          <span className="text-[#007185] font-semibold">Cart</span>
          <ChevronRight size={14} className="text-gray-400 shrink-0" />
          <span className="text-[#e77600] font-bold border-b border-[#e77600]">Delivery Details</span>
          <ChevronRight size={14} className="text-gray-400 shrink-0" />
          <span className="text-gray-400">Payment</span>
          <ChevronRight size={14} className="text-gray-400 shrink-0" />
          <span className="text-gray-400">Confirm</span>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        <div className="flex flex-col lg:flex-row gap-4">

          {/* LEFT COLUMN */}
          <div className="flex-1 space-y-3">

            {/* DELIVERY DETAILS */}
            <div className="bg-white border border-gray-200 rounded shadow-sm">
              <div className="flex items-center gap-3 px-4 py-3 bg-[#131921] rounded-t">
                <div className="w-7 h-7 rounded-full bg-[#FF9900] flex items-center justify-center text-[#131921] font-black text-sm shrink-0">1</div>
                <h2 className="text-white font-bold text-base">Delivery Details</h2>
                {customerSelected && (
                  <div className="ml-auto flex items-center gap-1.5 bg-emerald-500/20 border border-emerald-400/40 rounded-full px-2.5 py-1">
                    <CheckCircle size={11} className="text-emerald-400" />
                    <span className="text-[11px] font-bold text-emerald-300">Auto-filled</span>
                    <button type="button" onClick={() => setCustomerSelected(false)}
                      className="text-emerald-400 hover:text-white ml-0.5">
                      <X size={11} />
                    </button>
                  </div>
                )}
              </div>

              <div className="p-4 sm:p-5">
                {!customerSelected && (
                  <div className="flex items-center gap-2 bg-[#e8f4fd] border border-[#b3d9f0] rounded px-3 py-2.5 mb-4">
                    <Search size={13} className="text-[#007185] shrink-0" />
                    <p className="text-xs text-[#007185] font-medium">
                      Start typing in any field — existing customers will appear for quick auto-fill.
                    </p>
                  </div>
                )}

                <form id="checkout-form" onSubmit={handleSubmit}>

                  {/* Shop Image */}
                  <div className="mb-4">
                    <label className={labelCls}>Shop Image <span className="text-red-500">*</span></label>
                    <div className="border-2 border-dashed border-gray-300 rounded hover:border-[#e77600] transition-colors p-3 text-center cursor-pointer relative bg-gray-50">
                      <input type="file" accept="image/*"
                        onChange={(e) => handleFileChange(e, 'shopImage')}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                      {formData.shopImage
                        ? <p className="text-sm text-emerald-600 font-semibold">✓ {formData.shopImage.name}</p>
                        : <div>
                            <p className="text-xs text-gray-500 font-medium">Tap to upload shop photo</p>
                            <p className="text-[11px] text-gray-400 mt-0.5">JPG, PNG up to 5MB</p>
                          </div>
                      }
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                    {/* Shop Name */}
                    <SearchableInput
                      name="shopName" label="Shop Name" placeholder="e.g. Khadri Provision" required
                      value={formData.shopName}
                      onChange={handleInputChange}
                      onFocus={() => handleFieldFocus('shopName')}
                      onBlur={handleFieldBlur}
                      isActive={activeField === 'shopName'}
                      loading={loading} results={results} searchErr={searchErr}
                      onSelect={handleCustomerSelect}
                      customerSelected={customerSelected}
                      containerRef={refs.shopName}
                      inputCls={inputCls} labelCls={labelCls} autoFilledCls={autoFilledCls}
                    />

                    {/* Owner Name */}
                    <SearchableInput
                      name="ownerName" label="Owner Name" placeholder="Full Name" required
                      value={formData.ownerName}
                      onChange={handleInputChange}
                      onFocus={() => handleFieldFocus('ownerName')}
                      onBlur={handleFieldBlur}
                      isActive={activeField === 'ownerName'}
                      loading={loading} results={results} searchErr={searchErr}
                      onSelect={handleCustomerSelect}
                      customerSelected={customerSelected}
                      containerRef={refs.ownerName}
                      inputCls={inputCls} labelCls={labelCls} autoFilledCls={autoFilledCls}
                    />

                    {/* Mobile Number */}
                    <SearchableInput
                      name="mobileNumber" label="WhatsApp Mobile" type="tel"
                      placeholder="10-digit number" required maxLength={10} pattern="\d{10}"
                      value={formData.mobileNumber}
                      onChange={handleInputChange}
                      onFocus={() => handleFieldFocus('mobileNumber')}
                      onBlur={handleFieldBlur}
                      isActive={activeField === 'mobileNumber'}
                      loading={loading} results={results} searchErr={searchErr}
                      onSelect={handleCustomerSelect}
                      customerSelected={customerSelected}
                      containerRef={refs.mobileNumber}
                      inputCls={inputCls} labelCls={labelCls} autoFilledCls={autoFilledCls}
                    />

                    {/* FOS */}
                    <div>
                      <label className={labelCls}>FOS Person <span className="text-gray-400 font-normal">(Optional)</span></label>
                      <select name="fos" value={formData.fos} onChange={handleInputChange}
                        className={inputCls + ' cursor-pointer' + (customerSelected ? autoFilledCls : '')}>
                        <option value="">Select Executive</option>
                        <option value="Pavan Kumar">Pavan Kumar</option>
                        <option value="Kiran GS">Kiran GS</option>
                        <option value="Shivaraj">Shivaraj</option>
                        <option value="Kiran Kumar M">Gnanesh</option>
                        <option value="Muruli">Karan Singh</option>
                        <option value="Dharshan V">Dharshan V</option>
                        <option value="Basavaraj B ">Basavaraj B </option>
                        <option value="Bhaskar L">Bhaskar L</option>
                        <option value="Ramesh Babu">Ramesh Babu</option>
                        <option value="Punith">Punith</option>
                        <option value="Shivaram">Shivaram</option>
                        <option value="Fyntraxis">Testing - Fyntraxis</option>

                      </select>
                    </div>

                    {/* Address */}
                    <div className="col-span-1 sm:col-span-2">
                      <label className={labelCls}>Address <span className="text-red-500">*</span></label>
                      <textarea name="address" value={formData.address} onChange={handleInputChange}
                        required rows="2"
                        className={inputCls + ' resize-none' + (customerSelected ? autoFilledCls : '')}
                        placeholder="Complete shop address…" />
                    </div>

                    {/* Location */}
                    <div className="col-span-1 sm:col-span-2">
                      <label className={labelCls}>Location Coordinates <span className="text-red-500">*</span></label>
                      <div className="flex gap-2">
                        <input type="text" value={locationStr} readOnly required
                          className={inputCls + ' bg-gray-50 cursor-not-allowed flex-1'}
                          placeholder="Press Detect to fetch" />
                        <button type="button" onClick={detectLocation} disabled={isLocating}
                          className="shrink-0 bg-[#FF9900] hover:bg-[#e88a00] text-[#131921] font-bold px-4 py-2.5 rounded text-sm border border-[#d5a02a] active:scale-95 transition-all flex items-center gap-1.5 whitespace-nowrap">
                          {isLocating
                            ? <><Loader2 size={14} className="animate-spin" /><span>Wait</span></>
                            : <><MapPin size={14} /><span>Detect</span></>}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>

            {/* PAYMENT METHOD */}
            <div className="bg-white border border-gray-200 rounded shadow-sm">
              <div className="flex items-center gap-3 px-4 py-3 bg-[#131921] rounded-t">
                <div className="w-7 h-7 rounded-full bg-[#FF9900] flex items-center justify-center text-[#131921] font-black text-sm shrink-0">2</div>
                <h2 className="text-white font-bold text-base">Payment Method</h2>
              </div>

              <div className="p-4 sm:p-5 space-y-4">
                <div className="grid grid-cols-3 gap-2 sm:gap-3">
                  {[
                    { id: 'gpay',    label: 'Google Pay',       icon: '🟢', sub: 'UPI' },
                    { id: 'phonepe', label: 'PhonePe',           icon: '🟣', sub: 'UPI' },
                    { id: 'cod',     label: 'Cash on\nDelivery', icon: '💵', sub: 'COD' },
                  ].map((m) => (
                    <button key={m.id} type="button"
                      onClick={() => {
                        setPaymentMethod(m.id);
                        if (m.id === 'cod') { setPaymentType('full'); setPartialAmount(''); setScreenshot(null); }
                      }}
                      className={`py-3 px-2 rounded border-2 flex flex-col items-center justify-center gap-1 transition-all text-center ${
                        paymentMethod === m.id ? 'border-[#e77600] bg-[#fff8e7] shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}>
                      <div className={`w-3.5 h-3.5 rounded-full border-2 mb-0.5 flex items-center justify-center ${
                        paymentMethod === m.id ? 'border-[#e77600] bg-[#e77600]' : 'border-gray-300'}`}>
                        {paymentMethod === m.id && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                      </div>
                      <span className="text-base">{m.icon}</span>
                      <span className={`text-xs font-bold leading-tight whitespace-pre-line ${paymentMethod === m.id ? 'text-[#131921]' : 'text-gray-600'}`}>{m.label}</span>
                      <span className="text-[10px] text-gray-400 font-medium">{m.sub}</span>
                    </button>
                  ))}
                </div>

                {(paymentMethod === 'gpay' || paymentMethod === 'phonepe') && (
                  <div className="border border-[#e6d47a] rounded bg-[#fffbe8] p-4 space-y-4">
                    <div className="flex border border-gray-300 rounded overflow-hidden w-fit">
                      {['full', 'partial'].map((type) => (
                        <button key={type} type="button"
                          onClick={() => { setPaymentType(type); if (type === 'full') setPartialAmount(''); }}
                          className={`px-4 py-2 text-xs font-bold transition-all ${
                            paymentType === type ? 'bg-[#FF9900] text-[#131921]' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
                          {type === 'full' ? 'Pay in Full' : 'Partial Payment'}
                        </button>
                      ))}
                    </div>

                    {paymentType === 'partial' && (
                      <div>
                        <label className={labelCls}>Advance Amount <span className="text-gray-500 font-normal text-xs">(Min ₹100)</span> <span className="text-red-500">*</span></label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-bold">₹</span>
                          <input type="number" min="100" max={total} value={partialAmount}
                            onChange={(e) => setPartialAmount(e.target.value)} required
                            className={inputCls + ' pl-8 font-semibold'} placeholder="0.00" />
                        </div>
                        {Number(partialAmount) > total && (
                          <p className="text-xs text-red-500 font-semibold mt-1">Amount cannot exceed ₹{total.toFixed(2)}</p>
                        )}
                      </div>
                    )}

                    <div className="bg-white border border-gray-200 rounded p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-600">Transfer exactly</p>
                        <span className="text-xl font-black text-[#131921]">₹{amountToPay.toFixed(2)}</span>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <div className="flex-1 bg-gray-50 border border-gray-200 rounded px-3 py-2.5 font-mono text-xs font-bold text-gray-700 break-all">{UPI_ID}</div>
                        <button type="button" onClick={handlePayViaApp}
                          className="shrink-0 bg-[#FF9900] hover:bg-[#e88a00] text-[#131921] font-bold py-2.5 px-5 rounded text-sm border border-[#d5a02a] active:scale-95 transition-all whitespace-nowrap">
                          {paymentMethod === 'gpay' ? '▶ Open GPay' : '▶ Open PhonePe'}
                        </button>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <label className={labelCls}>Payment Proof Screenshot <span className="text-red-500">*</span></label>
                        <div className="border-2 border-dashed border-gray-300 rounded hover:border-[#e77600] transition-colors p-3 text-center cursor-pointer relative bg-white">
                          <input type="file" accept="image/*"
                            onChange={(e) => handleFileChange(e, 'screenshot')}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" required />
                          {screenshot
                            ? <p className="text-sm text-emerald-600 font-semibold">✓ {screenshot.name}</p>
                            : <p className="text-xs text-gray-500 font-medium">Tap to upload payment screenshot</p>
                          }
                        </div>
                      </div>
                      <div>
                        <label className={labelCls}>UTR / Transaction ID <span className="text-gray-400 font-normal text-xs">(Optional)</span></label>
                        <input type="text" value={transactionId} onChange={(e) => setTransactionId(e.target.value)}
                          className={inputCls + ' uppercase placeholder:normal-case'} placeholder="e.g. 312456789012" />
                      </div>
                    </div>
                  </div>
                )}

                {paymentMethod === 'cod' && (
                  <div className="flex items-start gap-3 bg-[#fff8e7] border border-[#e6d47a] rounded p-3">
                    <Truck size={18} className="text-[#e77600] shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-bold text-gray-800">Cash on Delivery selected</p>
                      <p className="text-xs text-gray-600 mt-0.5">Amount of <strong>₹{total.toFixed(2)}</strong> to be paid at delivery.</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* MOBILE SUBMIT */}
            <div className="lg:hidden">
              <button type="submit" form="checkout-form" disabled={isSubmitting}
                className="w-full bg-[#FF9900] hover:bg-[#e88a00] disabled:bg-[#f5c87a] text-[#131921] text-base font-bold py-4 rounded shadow-sm border border-[#d5a02a] active:scale-[0.98] transition-all flex justify-center items-center gap-2">
                {isSubmitting
                  ? <><Loader2 size={18} className="animate-spin" />Processing…</>
                  : paymentMethod === 'cod' ? '🛒 Confirm Order (COD)' : '🔒 Verify & Confirm Order'}
              </button>
              <p className="text-center text-xs text-gray-500 mt-2 flex items-center justify-center gap-1">
                <Lock size={10} /> Secure &amp; encrypted checkout
              </p>
            </div>
          </div>

          {/* RIGHT COLUMN — ORDER SUMMARY */}
          <div className="w-full lg:w-80 order-first lg:order-last">
            <div className="bg-white border border-gray-200 rounded shadow-sm lg:sticky lg:top-[72px]">
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t">
                <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
                  <Package size={16} className="text-[#e77600]" /> Order Summary
                </h3>
              </div>

              <div className="divide-y divide-gray-100 max-h-60 overflow-y-auto">
                {orderSummary.items.map((item) => (
                  <div key={item.id} className="flex items-center gap-3 px-4 py-3">
                    <div className="w-12 h-12 rounded border border-gray-200 bg-gray-50 shrink-0 overflow-hidden flex items-center justify-center">
                      {item.image
                        ? <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                        : <span className="text-[9px] text-gray-400 font-medium text-center leading-tight px-1">No Img</span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-900 truncate leading-tight">{item.name}</p>
                      {item.specs && <p className="text-[11px] text-gray-400 truncate">{item.specs}</p>}
                      <p className="text-[11px] text-gray-500 mt-0.5">Qty: <span className="font-bold">{item.qty}</span></p>
                    </div>
                    <span className="text-xs font-bold text-gray-900 shrink-0">₹{(item.price * item.qty).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <div className="px-4 py-3 border-t border-gray-200 space-y-2">
                <div className="flex justify-between text-xs text-gray-600">
                  <span>Subtotal ({orderSummary.items.length} items)</span>
                  <span className="font-semibold">₹{subtotal.toFixed(2)}</span>
                </div>
                {orderSummary.discount > 0 && (
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">Discount</span>
                    <span className="text-green-600 font-semibold">- ₹{orderSummary.discount.toFixed(2)}</span>
                  </div>
                )}
                {orderSummary.tax > 0 && (
                  <div className="flex justify-between text-xs text-gray-600">
                    <span>Tax</span>
                    <span className="font-semibold">₹{orderSummary.tax.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                  <span className="text-sm font-bold text-gray-900">Order Total</span>
                  <span className="text-lg font-black text-[#131921]">₹{total.toFixed(2)}</span>
                </div>
              </div>

              {paymentMethod !== 'cod' && paymentType === 'partial' && Number(partialAmount) >= 100 && (
                <div className="mx-4 mb-4 bg-[#fff8e7] border border-[#e6d47a] rounded p-3 space-y-1.5">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">Paying Now</span>
                    <span className="font-black text-[#131921]">₹{amountToPay.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-600">Balance Due</span>
                    <span className="font-bold text-red-600">₹{calculateBalance().toFixed(2)}</span>
                  </div>
                </div>
              )}

              {/* DESKTOP SUBMIT */}
              <div className="px-4 pb-4 hidden lg:block">
                <button type="submit" form="checkout-form" disabled={isSubmitting}
                  className="w-full bg-[#FF9900] hover:bg-[#e88a00] disabled:bg-[#f5c87a] text-[#131921] text-sm font-bold py-3 rounded shadow-sm border border-[#d5a02a] active:scale-[0.98] transition-all flex justify-center items-center gap-2">
                  {isSubmitting
                    ? <><Loader2 size={15} className="animate-spin" />Processing…</>
                    : paymentMethod === 'cod' ? '🛒 Confirm Order (COD)' : '🔒 Verify & Confirm'}
                </button>
                <p className="text-center text-[11px] text-gray-500 mt-2 flex items-center justify-center gap-1">
                  <Lock size={10} /> Secure &amp; encrypted checkout
                </p>
              </div>

              <div className="px-4 pb-4 flex items-center justify-center gap-4 text-[10px] text-gray-400">
                <span className="flex items-center gap-1"><ShieldCheck size={11} className="text-green-500" />Secure</span>
                <span className="flex items-center gap-1"><Star size={11} className="text-[#FF9900]" />Trusted</span>
                <span className="flex items-center gap-1"><Truck size={11} className="text-[#007185]" />Fast Delivery</span>
              </div>
            </div>
          </div>

        </div>
      </div>

      <footer className="mt-6 border-t border-gray-300 bg-[#131921] py-4 px-4">
        <div className="max-w-5xl mx-auto flex flex-wrap items-center justify-center gap-3 text-[11px] text-gray-400">
          <a href="#" className="hover:text-[#FF9900] hover:underline">Conditions of Use</a>
          <span>·</span>
          <a href="#" className="hover:text-[#FF9900] hover:underline">Privacy Notice</a>
          <span>·</span>
          <a href="#" className="hover:text-[#FF9900] hover:underline">Help</a>
        </div>
        <p className="text-center text-[11px] text-gray-500 mt-2">© 2024 Daily Go Pvt Ltd. All rights reserved.</p>
      </footer>

      <style>{`
        @media print {
          body * { visibility: hidden; }
          #printable-bill, #printable-bill * { visibility: visible; }
          #printable-bill { position: absolute; left: 0; top: 0; width: 100%; margin: 0; padding: 20px; }
          .no-print { display: none !important; }
        }
      `}</style>
    </div>
  );
};

export default PaymentPage;